import { useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiBox,
  MiButton,
  MiDetailPageHeader,
  MiIcon,
  MiLink,
  MiList,
} from "@miplatform/components";
import {
  BUTTON_LABELS,
  CONTACT_STATUSES,
  HTTP_STATUSES,
  ICONS_WEB,
  PAGINATION,
  PERMISSIONS,
} from "@miplatform/constants";
import { useDebounce, useRouter } from "@miplatform/hooks";
import { getPhoneNumber } from "@miplatform/utils";
import {
  mdiAccountReactivateOutline,
  mdiChevronLeft,
  mdiPencilOutline,
  mdiPlus,
} from "@mdi/js";
import * as mdi from "@mdi/js";
import { usePermissions } from "@mibuildweb/hooks";
import { useTheme } from "@mui/material";
import { AddContactDrawer } from "./AddContactDrawer/AddContactDrawer";
import { useManageContactViewModel } from "./useManageContactViewModel";

const contactList = [
  {
    field: "firstName",
    headerName: "First Name",
    flex: 1,
    renderCell: (item) => (
      <MiLink
        key={item.row.id}
        title={item.value}
        to={`/settings/contact-management/accounts/${item.row.accountId}/contacts/${item.row.id}`}
      />
    ),
  },
  {
    field: "lastName",
    headerName: "Last Name",
    flex: 1,
  },
  {
    field: "title",
    headerName: "Title",
    flex: 1,
  },
  { field: "accountName", headerName: "Account", flex: 1 },
  {
    field: "email",
    headerName: "Email",
    flex: 1,
  },
  {
    field: "phone",
    headerName: "Phone Number",
    valueGetter: (_, p) => {
      return p.phone ? getPhoneNumber(p.phone) : "";
    },
    flex: 1,
  },
  {
    field: "status",
    headerName: "Status",
    flex: 1,
  },
];

export const ManageContact = () => {
  const { classes } = useStyles();
  const [addDrawerOpen, setAddDrawerOpen] = useState(false);
  const { push } = useRouter();
  const [search, setSearch] = useState("");

  const debouncedSearch = useDebounce(search, 500);
  const theme = useTheme();
  const { hasPermission } = usePermissions();

  const hasEditPermission = hasPermission(PERMISSIONS.CONTACT.UPDATE);
  const hasCreatePermission = hasPermission(PERMISSIONS.CONTACT.CREATE);

  const { isLoading, contacts, getContacts, updateContactStatus } =
    useManageContactViewModel();

  const openAddDrawer = () => {
    setAddDrawerOpen(true);
  };

  const closeAddDrawer = () => {
    setAddDrawerOpen(false);
  };

  const getRowActions = (row) => {
    const actions = [];
    if (hasEditPermission) {
      actions.push({
        text: BUTTON_LABELS.EDIT,
        onClick: () => {
          navigateToContactDetails(row, { state: { isEditing: true } });
        },
        renderIcon: () => (
          <MiIcon
            path={mdiPencilOutline}
            size={1}
            color={theme.palette.green.main}
          />
        ),
      });

      if (row.status === CONTACT_STATUSES.ACTIVE) {
        actions.push({
          text: BUTTON_LABELS.DEACTIVATE,
          onClick: () =>
            handleUpdateContactStatus(row, CONTACT_STATUSES.INACTIVE),
          renderIcon: () => (
            <MiIcon
              path={mdi[ICONS_WEB.TRASH_CAN]}
              size={1}
              color={theme.palette.red.main}
            />
          ),
        });
      } else {
        actions.push({
          text: BUTTON_LABELS.ACTIVATE,
          onClick: () =>
            handleUpdateContactStatus(row, CONTACT_STATUSES.ACTIVE),
          renderIcon: () => (
            <MiIcon
              path={mdiAccountReactivateOutline}
              size={1}
              color={theme.palette.green.main}
            />
          ),
        });
      }
    }

    return actions;
  };

  const handleUpdateContactStatus = async (contact, status) => {
    const response = await updateContactStatus(contact, status);
    if (response.status === HTTP_STATUSES.OK) {
      getContacts();
    }
  };

  const navigateToContactDetails = (contact, settings) => {
    push(
      `/settings/contact-management/accounts/${contact.accountId}/contacts/${contact.id}`,
      settings
    );
  };

  useEffect(() => {
    getContacts();
  }, []);

  useEffect(() => {
    if (debouncedSearch?.length > 2) {
      getContacts(debouncedSearch);
    } else if (!debouncedSearch) {
      getContacts();
    }
  }, [debouncedSearch]);

  return (
    <div className={classes.contentContainer}>
      <MiDetailPageHeader
        title="Contacts"
        titleIcon={mdiChevronLeft}
        search={search}
        onHandleSearch={(e) => setSearch(e.target.value)}
        loading={isLoading}
        actionComponent={
          hasCreatePermission ? (
            <MiButton
              title="Add Contact"
              variant="contained"
              startIcon={{
                path: mdiPlus,
                iconSize: 0.8,
              }}
              data-testid="add-role-btn"
              onClick={openAddDrawer}
            />
          ) : null
        }
      />
      <MiBox className={classes.container}>
        <MiList
          columns={contactList}
          data={contacts}
          paginationMode={PAGINATION.CLIENT}
          loading={isLoading}
          getRowActions={getRowActions}
          onRowClick={(e) => navigateToContactDetails(e?.row)}
          data-testid="contacts-table"
        />
        <AddContactDrawer
          open={addDrawerOpen}
          onClose={closeAddDrawer}
          topRightClose={closeAddDrawer}
          loading={isLoading}
        />
      </MiBox>
    </div>
  );
};

const useStyles = makeStyles()(() => ({
  container: {
    height: "inherit",
    overflowY: "scroll",
  },
  contentContainer: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "100%",
  },
}));
